<template>
  <!-- Versicherungen sind systemseitig gesperrt: kein Schalter, nur ein
       Zustand mit Erklärung im Tooltip. Ein Schalter, der nichts bewirkt,
       wäre irreführend. -->
  <span v-if="insurance" class="pnt pnt--locked" :title="title">
    <component :is="iconBellOff" :size="15" />
  </span>
  <button
    v-else
    type="button"
    class="pnt"
    :class="{ 'pnt--muted': muted, 'pnt--inherit': caseMuted }"
    :disabled="busy || caseMuted"
    :title="title"
    :aria-pressed="muted ? 'true' : 'false'"
    @click="$emit('toggle')"
  >
    <component :is="muted || caseMuted ? iconBellOff : iconBell" :size="15" />
  </button>
</template>

<script>
/*
  Glocke je Beteiligtem in der Beteiligten-Liste
  (Feedback 15.09.2026, Teil 2: "Ich muss das aber auch bei den einzelnen
  Beteiligten einschalten und ausschalten können!").

  Die Glocke bleibt in jedem Zustand sichtbar und wechselt deutlich die Farbe.
  In einer langen Beteiligten-Liste übersieht man sonst, wer keine Mails mehr
  bekommt — ein nur ausgeblendetes Icon wäre kein Hinweis.

  Ist der ganze Fall stumm (Migration 0116), ist die Glocke gesperrt und zeigt
  den geerbten Zustand: Der stärkere Schalter gewinnt, die Einzelauswahl bleibt
  aber gespeichert und wirkt wieder, sobald der Fall-Schalter zurückgeht.
*/
import { Bell, BellOff } from "lucide-vue-next";

export default {
  name: "ParticipantNotificationToggle",

  props: {
    /** Anzeigename des Beteiligten — nur für den Tooltip. */
    name: { type: String, default: "" },
    /** true = dieser Beteiligte erhält keine Benachrichtigungen. */
    muted: { type: Boolean, default: false },
    /** true = der ganze Fall ist stumm (Migration 0116). */
    caseMuted: { type: Boolean, default: false },
    /** true, solange die Änderung gespeichert wird. */
    busy: { type: Boolean, default: false },
    /**
     * true = Versicherung. Erhält systemseitig nie Benachrichtigungen und hat
     * keinen Zugang zu KARS. Der Zustand ist nicht schaltbar.
     */
    insurance: { type: Boolean, default: false },
  },

  emits: ["toggle"],

  data() {
    return { iconBell: Bell, iconBellOff: BellOff };
  },

  computed: {
    title() {
      if (this.insurance) {
        return "Versicherungen erhalten grundsätzlich keine Benachrichtigungen und haben keinen Zugang zu KARS";
      }
      if (this.caseMuted) {
        return "Für den ganzen Fall sind Benachrichtigungen aus — diese Einstellung wirkt wieder, sobald sie eingeschaltet sind";
      }
      const who = this.name || "diesen Beteiligten";
      return this.muted
        ? `${who} erhält keine Benachrichtigungen — wieder einschalten`
        : `Benachrichtigungen für ${who} ausschalten`;
    },
  },
};
</script>

<style scoped>
.pnt {
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  color: var(--grey-color);
  transition: all 0.15s;
  display: flex;
  align-items: center;
}

.pnt:hover:not(:disabled) {
  background: var(--grey-light-color);
  color: var(--primary-color);
}

/* Stummgeschaltet: klar erkennbar, nicht nur "irgendwie anders". */
.pnt--muted {
  background: #fee2e2;
  color: #b91c1c;
}

.pnt--muted:hover:not(:disabled) {
  background: #fecaca;
  color: #991b1b;
}

/* Zustand vom Fall geerbt — bedienbar wird die Glocke erst wieder, wenn der
   Fall-Schalter an ist. */
.pnt--inherit {
  opacity: 0.45;
}

.pnt:disabled {
  cursor: default;
}

/* Systemseitig gesperrt (Versicherung). Bewusst neutral-grau statt rot: Das
   ist kein Zustand, den jemand gesetzt hat und zurücknehmen könnte. */
.pnt--locked {
  color: #94a3b8;
  cursor: help;
}
</style>
